import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Typography, Space, message, Dropdown, Modal, Checkbox, Row, Col, Progress } from 'antd';
import { HomeOutlined, EditOutlined, CheckOutlined, CloseOutlined, LeftOutlined, RightOutlined, DeleteOutlined, MoreOutlined, CarOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { addBlock, addApartmentUnitsBulk, addParkingBulk } from '@/api/properties';
import { incrementBlockCount, initializeBlockStats } from '@/api/workspaces';
import { Block, Floor, ApartmentUnit, Parking } from '@/types/resources/property';

const { Text, Title } = Typography;

interface BlockFormData {
  name: string;
  floors: number;
  unitsPerFloor: number;
  deletedFloors?: number[]; // Array of floor indices that are deleted
  deletedUnits?: { [floorIndex: number]: number[] }; // Object mapping floor index to deleted unit indices
  addedUnits?: { [floorIndex: number]: number[] }; // Object mapping floor index to added unit indices (beyond base unitsPerFloor)
  customFloorNames?: { [floorIndex: number]: string }; // Object mapping floor index to custom floor name
  customUnitNames?: { [floorIndex: number]: { [unitIndex: number]: string } }; // Object mapping floor and unit indices to custom unit name
}

interface IndividualBlocksCardProps {
  numberOfBlocks: number;
  defaultLayout?: {
    floors: number;
    unitsPerFloor: number;
  } | null;
  openSummaryOnMount?: boolean;
  onNext: (blocksData: BlockFormData[], includeParking?: boolean, createdBlockIds?: string[]) => void;
  onBack: () => void;
}

export const IndividualBlocksCard: React.FC<IndividualBlocksCardProps> = ({
  numberOfBlocks,
  defaultLayout,
  openSummaryOnMount = false,
  onNext,
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { firestoreUser, refreshWorkspaceStats } = useAuth();
  const [form] = Form.useForm();
  const [blocksData, setBlocksData] = useState<BlockFormData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingBlockName, setEditingBlockName] = useState<string>('');
  const [currentBlockIndex, setCurrentBlockIndex] = useState<number>(0);
  const [editingFloorIndex, setEditingFloorIndex] = useState<number | null>(null);
  const [editingFloorName, setEditingFloorName] = useState<string>('');
  const [unitNamingConvention, setUnitNamingConvention] = useState<string>('floor-alphabet');
  const [customPrefix, setCustomPrefix] = useState<string>('');
  const [editingUnit, setEditingUnit] = useState<{blockIndex: number, floorIndex: number, unitIndex: number} | null>(null);
  const [editingUnitName, setEditingUnitName] = useState<string>('');
  const [includeParking, setIncludeParking] = useState<boolean>(false);
  
  // Scroll to top when currentBlockIndex changes
  useEffect(() => {
    if (currentBlockIndex > 0) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);
    }
  }, [currentBlockIndex]);

  useEffect(() => {
    if (!openSummaryOnMount) return;
    setCurrentBlockIndex(numberOfBlocks);
  }, [openSummaryOnMount, numberOfBlocks]);

  // Generate unit number based on naming convention
  const generateUnitNumber = (floorNumber: number, unitIndex: number) => {
    switch (unitNamingConvention) {
      case 'floor-alphabet':
        return `${floorNumber}${String.fromCharCode(65 + unitIndex)}`;
      case 'floor-number':
        return `${floorNumber}${unitIndex + 1}`;
      case 'three-digit':
        return `${floorNumber}${String(unitIndex + 1).padStart(2, '0')}`;
      case 'custom':
        if (customPrefix) {
          return `${customPrefix}${String(floorNumber).padStart(2, '0')}${String(unitIndex + 1).padStart(2, '0')}`;
        }
        return `Unit ${unitIndex + 1}`;
      default:
        return `${floorNumber}${String.fromCharCode(65 + unitIndex)}`;
    }
  };

  // Generate floors using the same logic as BlockBulkUploadModal
  const generateFloors = (blockName: string, totalFloors: number, unitsPerFloor: number): Floor[] => {
    return Array.from({ length: totalFloors }, (_, index) => {
      const floorNumber = totalFloors - index; // Reverse order to match UI (top floor first)
      const name = t('onboarding:cards.individualBlocks.floorName', { number: floorNumber });
      const slug = `${blockName.toLowerCase()}-floor-${floorNumber}`;
      
      // Generate apartment units for this floor
      const apartmentUnits = Array.from({ length: unitsPerFloor }, (_, unitIndex) => {
        const unitNumber = generateUnitNumber(floorNumber, unitIndex);
        
        return {
          number: unitNumber,
          name: `Unit ${unitNumber}`,
          slug: `${slug}-unit-${unitNumber}`
        };
      });
      
      return {
        name,
        slug,
        description: '',
        apartmentUnits
      };
    });
  };

  // Transform onboarding block data to API format
  const transformBlockData = (blockData: BlockFormData): {
    block: Omit<Block, 'id' | 'workspace' | 'activityRecords' | 'images'> & { 
      unitsPerFloor: number; 
      createParking: boolean 
    };
    floors: Floor[];
    totalUnits: number;
    totalParkings: number;
  } => {
    const deletedFloors = blockData.deletedFloors?.length || 0;
    const activeFloors = blockData.floors - deletedFloors;
    const deletedUnits = blockData.deletedUnits || {};
    const addedUnits = blockData.addedUnits || {};
    
    // Calculate total units including added units and excluding deleted units
    const totalUnits = Array.from({ length: blockData.floors }, (_, floorIndex) => {
      if (blockData.deletedFloors?.includes(floorIndex)) return 0;
      const deletedUnitsForFloor = deletedUnits[floorIndex] || [];
      const addedUnitsForFloor = addedUnits[floorIndex] || [];
      return blockData.unitsPerFloor - deletedUnitsForFloor.length + addedUnitsForFloor.length;
    }).reduce((sum, units) => sum + units, 0);

    // Generate floors with proper unit numbering
    const floors = generateFloors(blockData.name, activeFloors, blockData.unitsPerFloor);
    
    // Apply custom unit names and handle added/deleted units
    floors.forEach((floor, floorIndex) => {
      const originalFloorIndex = blockData.floors - 1 - floorIndex; // Convert to original floor index
      const customUnitNames = blockData.customUnitNames?.[originalFloorIndex] || {};
      const deletedUnitsForFloor = deletedUnits[originalFloorIndex] || [];
      const addedUnitsForFloor = addedUnits[originalFloorIndex] || [];
      
      // Apply custom names to existing units
      floor.apartmentUnits?.forEach((unit, unitIndex) => {
        if (customUnitNames[unitIndex]) {
          unit.number = customUnitNames[unitIndex];
          unit.name = `Unit ${customUnitNames[unitIndex]}`;
        }
      });
      
      // Remove deleted units
      if (deletedUnitsForFloor.length > 0) {
        floor.apartmentUnits = floor.apartmentUnits?.filter((_, unitIndex) => 
          !deletedUnitsForFloor.includes(unitIndex)
        );
      }
      
      // Add extra units if any
      if (addedUnitsForFloor.length > 0) {
        addedUnitsForFloor.forEach(addedUnitIndex => {
          const unitNumber = generateUnitNumber(blockData.floors - originalFloorIndex, addedUnitIndex);
          floor.apartmentUnits?.push({
            number: unitNumber,
            name: `Unit ${unitNumber}`,
            slug: `${floor.slug}-unit-${unitNumber}`
          });
        });
      }
    });

    return {
      block: {
        name: blockData.name,
        description: t('onboarding:cards.individualBlocks.blockDescription', { 
          name: blockData.name, 
          floors: activeFloors, 
          units: totalUnits 
        }),
        floors: [], // We'll create floors separately
        unitsPerFloor: blockData.unitsPerFloor,
        createParking: includeParking
      },
      floors,
      totalUnits,
      totalParkings: includeParking ? totalUnits : 0
    };
  };

  // Initialize blocks data based on numberOfBlocks
  useEffect(() => {
    const defaultFloors = defaultLayout?.floors ?? 2;
    const defaultUnitsPerFloor = defaultLayout?.unitsPerFloor ?? 2;
    const initialData = Array.from({ length: numberOfBlocks }, (_, index) => ({
      name: t('onboarding:cards.individualBlocks.blockNumber', { number: index + 1 }),
      floors: defaultFloors,
      unitsPerFloor: defaultUnitsPerFloor,
      deletedFloors: [],
      deletedUnits: {},
      addedUnits: {},
      customFloorNames: {},
      customUnitNames: {},
    }));
    setBlocksData(initialData);
    form.setFieldsValue({
      blocks: initialData,
    });
  }, [numberOfBlocks, form, defaultLayout, t]);

  // Update form values when blocksData changes
  useEffect(() => {
    if (blocksData.length > 0) {
      form.setFieldsValue({
        blocks: blocksData,
      });
    }
  }, [blocksData, form]);

  // Generate floor options (2-20)
  const floorOptions = Array.from({ length: 19 }, (_, i) => ({
    value: i + 2,
    label: t('onboarding:cards.individualBlocks.floorsOption', { count: i + 2 }),
  }));

  // Generate units per floor options (2-10)
  const unitsPerFloorOptions = Array.from({ length: 9 }, (_, i) => ({
    value: i + 2,
    label: t('onboarding:cards.individualBlocks.unitsOption', { count: i + 2 }),
  }));

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const validatedBlocks = values.blocks.map((block: BlockFormData, index: number) => ({
        ...block,
        name: block.name.trim() || t('onboarding:cards.individualBlocks.blockNumber', { number: index + 1 }),
      }));
      
      setBlocksData(validatedBlocks);
      
      // Check if there are more blocks to configure
      if (currentBlockIndex < numberOfBlocks - 1) {
        // Copy current block configuration to next block
        const nextBlockIndex = currentBlockIndex + 1;
        const updatedBlocks = [...validatedBlocks];
        
        // Copy all configuration from current block to next block
        updatedBlocks[nextBlockIndex] = {
          ...updatedBlocks[currentBlockIndex],
          name: t('onboarding:cards.individualBlocks.blockNumber', { number: nextBlockIndex + 1 }), // Reset name to default
        };
        
        setBlocksData(updatedBlocks);
        
        // Update form values
        form.setFieldsValue({
          blocks: updatedBlocks,
        });
        
        // Move to next block in carousel
        setCurrentBlockIndex(nextBlockIndex);
      } else {
        // All blocks completed, navigate to summary (last carousel item)
        setBlocksData(validatedBlocks);
        setCurrentBlockIndex(numberOfBlocks); // Summary is at index numberOfBlocks
      }
    } catch (error) {
      console.error('Form validation failed:', error);
      message.error(t('common:validation.requiredFields'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditBlockName = (index: number) => {
    setEditingBlockIndex(index);
    setEditingBlockName(blocksData[index]?.name || t('onboarding:cards.individualBlocks.blockNumber', { number: index + 1 }));
  };

  const handleSaveBlockName = (index: number) => {
    if (editingBlockName.trim()) {
      const updatedBlocks = [...blocksData];
      updatedBlocks[index] = { ...updatedBlocks[index], name: editingBlockName.trim() };
      setBlocksData(updatedBlocks);
      
      // Update form values
      const formValues = form.getFieldsValue();
      formValues.blocks[index].name = editingBlockName.trim();
      form.setFieldsValue(formValues);
    }
    setEditingBlockIndex(null);
    setEditingBlockName('');
  };

  const handleCancelEdit = () => {
    setEditingBlockIndex(null);
    setEditingBlockName('');
  };

  const handlePreviousBlock = () => {
    if (currentBlockIndex > 0) {
      setCurrentBlockIndex(currentBlockIndex - 1);
    }
  };

  const handleNextBlock = () => {
    if (currentBlockIndex < numberOfBlocks) {
      if (currentBlockIndex < numberOfBlocks - 1) {
        handleNavigateToBlock(currentBlockIndex + 1);
      } else if (currentBlockIndex === numberOfBlocks - 1) {
        // Navigate to summary
        setCurrentBlockIndex(numberOfBlocks);
      }
    }
  };

  const handleNavigateToBlock = (targetIndex: number) => {
    if (targetIndex >= 0 && targetIndex <= numberOfBlocks) {
      if (targetIndex < numberOfBlocks) {
        // Copy current block configuration to target block if it's empty/default
        const currentBlock = blocksData[currentBlockIndex];
        const targetBlock = blocksData[targetIndex];
        
        // Check if target block has default configuration (2 floors, 2 units)
        if (targetBlock && targetBlock.floors === 2 && targetBlock.unitsPerFloor === 2 && 
            targetBlock.name === `Block ${targetIndex + 1}`) {
          const updatedBlocks = [...blocksData];
          updatedBlocks[targetIndex] = {
            ...currentBlock,
            name: t('onboarding:cards.individualBlocks.blockNumber', { number: targetIndex + 1 }), // Reset name to default
          };
          
          setBlocksData(updatedBlocks);
          form.setFieldsValue({
            blocks: updatedBlocks,
          });
        }
      }
      
      setCurrentBlockIndex(targetIndex);
    }
  };

  const handleEditFloorName = (floorIndex: number) => {
    setEditingFloorIndex(floorIndex);
    const currentBlock = blocksData[currentBlockIndex];
    const currentName = currentBlock?.customFloorNames?.[floorIndex] || t('onboarding:cards.individualBlocks.floorName', { number: currentBlock?.floors - floorIndex });
    setEditingFloorName(currentName);
  };

  const handleSaveFloorName = (floorIndex: number) => {
    if (editingFloorName.trim()) {
      const newBlocksData = [...blocksData];
      const currentBlock = newBlocksData[currentBlockIndex];
      
      // Initialize customFloorNames if it doesn't exist
      if (!currentBlock.customFloorNames) {
        currentBlock.customFloorNames = {};
      }
      
      // Save the custom floor name
      currentBlock.customFloorNames[floorIndex] = editingFloorName.trim();
      
      setBlocksData(newBlocksData);
      message.success(t('common:messages.updated'));
    }
    setEditingFloorIndex(null);
    setEditingFloorName('');
  };

  const handleCancelFloorEdit = () => {
    setEditingFloorIndex(null);
    setEditingFloorName('');
  };

  // Handle floor deletion
  const handleDeleteFloor = (blockIndex: number, floorIndex: number) => {
    Modal.confirm({
      title: t('common:actions.delete'),
      content: t('common:confirmations.delete'),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: () => {
        const newBlocksData = [...blocksData];
        const block = newBlocksData[blockIndex];
        const deletedFloors = [...(block.deletedFloors || [])];
        
        // Add the floor to deleted floors list
        if (!deletedFloors.includes(floorIndex)) {
          deletedFloors.push(floorIndex);
        }
        
        newBlocksData[blockIndex] = {
          ...block,
          deletedFloors: deletedFloors
        };
        setBlocksData(newBlocksData);
        form.setFieldsValue({
          blocks: newBlocksData,
        });
      },
    });
  };

  // Handle unit edit
  const handleEditUnit = (blockIndex: number, floorIndex: number, unitIndex: number) => {
    const block = blocksData[blockIndex];
    const floorNumber = block.floors - floorIndex;
    const defaultUnitName = generateUnitNumber(floorNumber, unitIndex);
    const currentUnitName = block.customUnitNames?.[floorIndex]?.[unitIndex] || defaultUnitName;
    setEditingUnit({ blockIndex, floorIndex, unitIndex });
    setEditingUnitName(currentUnitName);
  };

  // Handle unit save
  const handleSaveUnit = () => {
    if (editingUnit && editingUnitName.trim()) {
      const newBlocksData = [...blocksData];
      const currentBlock = newBlocksData[editingUnit.blockIndex];
      
      // Initialize customUnitNames if it doesn't exist
      if (!currentBlock.customUnitNames) {
        currentBlock.customUnitNames = {};
      }
      if (!currentBlock.customUnitNames[editingUnit.floorIndex]) {
        currentBlock.customUnitNames[editingUnit.floorIndex] = {};
      }
      
      // Save the custom unit name
      currentBlock.customUnitNames[editingUnit.floorIndex][editingUnit.unitIndex] = editingUnitName.trim();
      
      setBlocksData(newBlocksData);
      setEditingUnit(null);
      setEditingUnitName('');
      message.success(t('common:messages.updated'));
    }
  };

  // Handle unit cancel
  const handleCancelUnitEdit = () => {
    setEditingUnit(null);
    setEditingUnitName('');
  };

  const handleSummaryNext = async () => {
    if (!firestoreUser) {
      message.error(t('common:errors.notAuthenticated'));
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Transform blocks data to API format
      const processedData = blocksData.map(blockData => transformBlockData(blockData));
      
      let totalUploaded = 0;
      let totalApartmentUnits = 0;
      let totalParkings = 0;
      const totalRecords = processedData.length;
      const createdBlockIds: string[] = [];
      
      // Show progress modal
      const progressModal = Modal.info({
        title: t('onboarding:cards.individualBlocks.creatingBlocks'),
        content: (
          <div>
            <Progress 
              percent={0} 
              status="active" 
              format={(percent) => `${totalUploaded}/${totalRecords} (${percent}%)`}
            />
            <p>{t('onboarding:cards.individualBlocks.creatingBlocksProgress')}</p>
            <p>This will also create apartment units and parking spaces automatically.</p>
          </div>
        ),
        okButtonProps: { style: { display: 'none' } }
      });

      // Process each block individually to get the block ID for creating units and parking
      for (let blockIndex = 0; blockIndex < processedData.length; blockIndex++) {
        const data = processedData[blockIndex];
        const blockData = data.block;
        
        try {
          // Step 1: Create the block with floors data
          const blockWithFloors = {
            name: blockData.name,
            description: blockData.description,
            floors: data.floors
          };

          progressModal.update({
            content: (
              <div>
                <Progress 
                  percent={Math.round((blockIndex / totalRecords) * 100)} 
                  status="active" 
                  format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                />
                <p>Creating block: {blockData.name}</p>
                <p>Progress: {blockIndex + 1} of {totalRecords}</p>
              </div>
            )
          });

          // Create block using the existing API (this will return the block ID)
          const blockId = await addBlock(blockWithFloors, firestoreUser);

          if (!blockId) {
            throw new Error(`Failed to create block: ${blockData.name}`);
          }

          // Store the created block ID
          createdBlockIds.push(blockId);
          totalUploaded++;
          
          // Step 2: Create apartment units for this block
          const apartmentUnits: Omit<ApartmentUnit, 'id' | 'workspace' | 'activityRecords'>[] = [];
          const parkingUnits: Omit<Parking, 'id' | 'workspace' | 'activityRecords'>[] = [];
          
          if (data.floors) {
            data.floors.forEach(floor => {
              floor.apartmentUnits?.forEach(unit => {
                // Add apartment unit
                apartmentUnits.push({
                  number: unit.number,
                  status: 'vacant',
                  block: {
                    id: blockId,
                    name: blockData.name
                  },
                  floor: {
                    name: floor.name,
                    slug: floor.slug
                  },
                  description: `Unit ${unit.number} on ${floor.name}`
                });

                // Add parking unit if createParking is true
                if (blockData.createParking) {
                  parkingUnits.push({
                    number: `${blockData.name}-${unit.number}`,
                    type: 'car',
                    status: 'available',
                    block: {
                      id: blockId,
                      name: blockData.name
                    },
                    apartmentUnit: {
                      id: '', // Will be filled by the system
                      number: unit.number
                    },
                    description: `Parking for Unit ${unit.number} on ${floor.name}`
                  });
                }
              });
            });
          }

          // Create apartment units in bulk
          if (apartmentUnits.length > 0) {
            progressModal.update({
              content: (
                <div>
                  <Progress 
                    percent={Math.round((blockIndex / totalRecords) * 100)} 
                    status="active" 
                    format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                  />
                  <p>{t('onboarding:cards.individualBlocks.creatingUnitsProgress', { blockName: blockData.name })}</p>
                  <p>{t('onboarding:cards.individualBlocks.totalUnitsToCreate', { count: apartmentUnits.length })}</p>
                </div>
              )
            });

            await addApartmentUnitsBulk(apartmentUnits, firestoreUser, () => {
              // Progress callback for apartment units
            });
            
            totalApartmentUnits += apartmentUnits.length;
          }

          // Create parking units in bulk
          if (parkingUnits.length > 0) {
            progressModal.update({
              content: (
                <div>
                  <Progress 
                    percent={Math.round((blockIndex / totalRecords) * 100)} 
                    status="active" 
                    format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                  />
                  <p>Creating parking spaces for: {blockData.name}</p>
                  <p>{t('onboarding:cards.individualBlocks.summary.progressMessages.totalParkingSpaces', { count: parkingUnits.length })}</p>
                </div>
              )
            });

            await addParkingBulk(parkingUnits, firestoreUser, () => {
              // Progress callback for parking units
            });
            
            totalParkings += parkingUnits.length;
          }

          // Step 3: Update workspace stats for this block
          const totalUnits = data.totalUnits;
          const totalParkingSpaces = blockData.createParking ? totalUnits : 0;

          try {
            // Initialize block stats
            await initializeBlockStats(firestoreUser, blockId, blockData.name);
            
            // Update block-specific stats
            await incrementBlockCount(firestoreUser, blockId, 'totalApartmentUnits', totalUnits);
            if (blockData.createParking) {
              await incrementBlockCount(firestoreUser, blockId, 'totalParkings', totalParkingSpaces);
            }
          } catch (error) {
            console.warn(`Failed to update stats for block ${blockId}:`, error);
          }

          // Update progress
          const percent = Math.round(((blockIndex + 1) / totalRecords) * 100);
          progressModal.update({
            content: (
              <div>
                <Progress 
                  percent={percent} 
                  status="active" 
                  format={(percent) => `${blockIndex + 1}/${totalRecords} (${percent}%)`}
                />
                <p>Completed: {blockData.name}</p>
                <p>Progress: {blockIndex + 1} of {totalRecords}</p>
                <p>Blocks: {totalUploaded}, Units: {totalApartmentUnits}, Parking: {totalParkings}</p>
              </div>
            )
          });

        } catch (error) {
          console.error(`Error processing block ${blockData.name}:`, error);
          message.error(`Error processing block ${blockData.name}. Continuing with next block...`);
        }
      }

      // Refresh workspace stats
      try {
        await refreshWorkspaceStats();
      } catch (error) {
        console.warn('Failed to refresh workspace stats:', error);
      }

      progressModal.destroy();
      message.success(`Successfully created ${totalUploaded} blocks with ${totalApartmentUnits} apartment units and ${totalParkings} parking spaces`);
      
      // Call the original onNext to proceed to the next step
      onNext(blocksData, includeParking, createdBlockIds);
      
    } catch (error) {
      console.error('Error creating blocks:', error);
      message.error(t('common:errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSummaryBack = () => {
    setCurrentBlockIndex(numberOfBlocks - 1); // Go back to last block
  };

  // Handle unit deletion
  const handleDeleteUnit = (blockIndex: number, floorIndex: number, unitIndex: number) => {
    Modal.confirm({
      title: t('common:actions.delete'),
      content: t('common:confirmations.delete'),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: () => {
        const newBlocksData = [...blocksData];
        const block = newBlocksData[blockIndex];
        const deletedUnits = { ...(block.deletedUnits || {}) };
        
        // Initialize deleted units array for this floor if it doesn't exist
        if (!deletedUnits[floorIndex]) {
          deletedUnits[floorIndex] = [];
        }
        
        // Add the unit to deleted units list for this floor
        if (!deletedUnits[floorIndex].includes(unitIndex)) {
          deletedUnits[floorIndex].push(unitIndex);
        }
        
        newBlocksData[blockIndex] = {
          ...block,
          deletedUnits: deletedUnits
        };
        setBlocksData(newBlocksData);
        form.setFieldsValue({
          blocks: newBlocksData,
        });
        message.success(t('common:messages.deleted'));
      },
    });
  };

  // Handle adding new floor
  const handleAddFloor = (blockIndex: number) => {
    const updatedBlocks = [...blocksData];
    updatedBlocks[blockIndex].floors += 1;
    
    setBlocksData(updatedBlocks);
    form.setFieldsValue({
      blocks: updatedBlocks,
    });
    
    message.success(t('common:messages.created'));
  };

  // Handle adding new unit to a specific floor only
  const handleAddUnitToFloor = (blockIndex: number, floorIndex: number) => {
    const updatedBlocks = [...blocksData];
    const block = updatedBlocks[blockIndex];
    
    // Initialize addedUnits if it doesn't exist
    if (!block.addedUnits) {
      block.addedUnits = {};
    }
    if (!block.addedUnits[floorIndex]) {
      block.addedUnits[floorIndex] = [];
    }
    
    // Find the next available unit index that doesn't already exist
    let nextUnitIndex = block.unitsPerFloor;
    const existingAddedUnits = block.addedUnits[floorIndex];
    const deletedUnits = block.deletedUnits?.[floorIndex] || [];
    
    // Find the first available index that's not already added and not deleted
    while (
      existingAddedUnits.includes(nextUnitIndex) || 
      deletedUnits.includes(nextUnitIndex)
    ) {
      nextUnitIndex++;
    }
    
    // Add the new unit index to this specific floor
    block.addedUnits[floorIndex].push(nextUnitIndex);
    
    setBlocksData(updatedBlocks);
    form.setFieldsValue({
      blocks: updatedBlocks,
    });
    
    message.success(t('common:messages.created'));
  };

  // Handle adding new unit to all floors
  const handleAddUnitToAllFloors = (blockIndex: number) => {
    const updatedBlocks = [...blocksData];
    const block = updatedBlocks[blockIndex];
    
    // When adding to all floors, we need to shift existing added units to avoid conflicts
    const newUnitsPerFloor = block.unitsPerFloor + 1;
    
    // Shift all existing added units by 1 to make room for the new base unit
    if (block.addedUnits) {
      const updatedAddedUnits: { [key: number]: number[] } = {};
      Object.keys(block.addedUnits).forEach(floorIndex => {
        const floorIdx = parseInt(floorIndex);
        updatedAddedUnits[floorIdx] = block.addedUnits![floorIdx].map(unitIndex => unitIndex + 1);
      });
      block.addedUnits = updatedAddedUnits;
    }
    
    block.unitsPerFloor = newUnitsPerFloor;
    
    setBlocksData(updatedBlocks);
    form.setFieldsValue({
      blocks: updatedBlocks,
    });
    
    message.success(t('common:messages.created'));
  };

  // Handle deleting an added unit
  const handleDeleteAddedUnit = (blockIndex: number, floorIndex: number, unitIndex: number) => {
    Modal.confirm({
      title: t('common:actions.delete'),
      content: t('common:confirmations.delete'),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: () => {
        const newBlocksData = [...blocksData];
        const block = newBlocksData[blockIndex];
        const addedUnits = { ...(block.addedUnits || {}) };
        
        // Initialize added units array for this floor if it doesn't exist
        if (!addedUnits[floorIndex]) {
          addedUnits[floorIndex] = [];
        }
        
        // Remove the unit from added units list for this floor
        const unitIndexInArray = addedUnits[floorIndex].indexOf(unitIndex);
        if (unitIndexInArray > -1) {
          addedUnits[floorIndex].splice(unitIndexInArray, 1);
        }
        
        newBlocksData[blockIndex] = {
          ...block,
          addedUnits: addedUnits
        };
        setBlocksData(newBlocksData);
        form.setFieldsValue({
          blocks: newBlocksData,
        });
        message.success(t('common:messages.deleted'));
      },
    });
  };

  const handleFormValuesChange = (_changedValues: any, _allValues: any) => {
    // Update blocksData when form values change
    if (_allValues.blocks) {
      const updatedBlocks = _allValues.blocks.map((block: any, index: number) => ({
        name: block.name !== undefined ? block.name : (blocksData[index]?.name || t('onboarding:cards.individualBlocks.blockNumber', { number: index + 1 })),
        floors: block.floors !== undefined ? block.floors : (blocksData[index]?.floors || 2),
        unitsPerFloor: block.unitsPerFloor !== undefined ? block.unitsPerFloor : (blocksData[index]?.unitsPerFloor || 2),
      }));
      setBlocksData(updatedBlocks);
    }
  };

  const renderApartmentGrid = (blockIndex: number) => {
    const block = blocksData[blockIndex];
    if (!block || !block.floors || !block.unitsPerFloor) return null;

    const floors = block.floors;
    const unitsPerFloor = block.unitsPerFloor;
    const deletedFloors = block.deletedFloors || [];
    const deletedUnits = block.deletedUnits || {};
    
    return (
      <div style={{ 
        border: '2px solid #d9d9d9',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Floor Plan Content */}
        <div style={{ 
          padding: '8px'
        }}>
          {/* Add Floor Button - At Top */}
          <div style={{ 
            backgroundColor: '#fff',
            borderRadius: '6px',
            border: '1px solid #e8e8e8',
            marginBottom: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '12px',
              textAlign: 'center'
            }}>
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleAddFloor(blockIndex)}
                style={{
                  border: '2px dashed #d9d9d9',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#1890ff',
                  backgroundColor: 'transparent',
                  height: '32px',
                  padding: '0 16px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1890ff';
                  e.currentTarget.style.backgroundColor = '#f6ffed';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d9d9d9';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
{t('onboarding:cards.individualBlocks.addFloor')}
              </Button>
            </div>
          </div>
          
          {Array.from({ length: floors }, (_, floorIndex) => {
            // Skip deleted floors
            if (deletedFloors.includes(floorIndex)) {
              return null;
            }
            
            // Reverse the floor index to show higher floors on top
            const displayFloorIndex = floors - 1 - floorIndex;
            
            return (
            <div key={`block-${blockIndex}-floor-${floorIndex}`} style={{ 
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: '1px solid #e8e8e8',
              marginBottom: '8px',
              overflow: 'hidden'
            }}>
                                  {/* Floor Name Row */}
                    <div style={{ 
                      padding: '4px 8px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      {editingFloorIndex === displayFloorIndex ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Input
                            size="small"
                            value={editingFloorName}
                            onChange={(e) => setEditingFloorName(e.target.value)}
                            style={{ 
                              fontSize: '11px',
                              fontWeight: '500',
                              textAlign: 'left',
                              minWidth: '80px',
                              height: '20px'
                            }}
                            onPressEnter={() => handleSaveFloorName(displayFloorIndex)}
                            autoFocus
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={() => handleSaveFloorName(displayFloorIndex)}
                            style={{ color: '#52c41a', padding: '0', height: '20px' }}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleCancelFloorEdit}
                            style={{ color: '#ff4d4f', padding: '0', height: '20px' }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px'
                        }}>
                          <Text style={{ 
                            fontSize: '11px',
                            fontWeight: '500',
                            color: '#666'
                          }}>
{block.customFloorNames?.[displayFloorIndex] || t('onboarding:cards.individualBlocks.floorNumber', { number: floors - displayFloorIndex })}
                          </Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditFloorName(displayFloorIndex)}
                            style={{ color: '#666', padding: '0', height: '20px' }}
                          />
                        </div>
                      )}
                      
                      {/* Delete Floor Button */}
                      {deletedFloors.length < floors - 1 && (
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteFloor(blockIndex, displayFloorIndex)}
                          style={{ color: '#ff4d4f', padding: '0', height: '20px' }}
                        />
                      )}
                    </div>

              {/* Apartment Units Row */}
              <div style={{ 
                padding: '12px',
                overflowX: 'auto',
                overflowY: 'visible'
              }}>
                <div style={{ 
                  display: 'flex',
                  gap: '6px',
                  minWidth: 'fit-content'
                }}>
                  {/* Render base units */}
                  {Array.from({ length: unitsPerFloor }, (_, unitIndex) => {
                    // Skip deleted units for this floor
                    if (deletedUnits[displayFloorIndex] && deletedUnits[displayFloorIndex].includes(unitIndex)) {
                      return null;
                    }
                    
                    const floorNumber = floors - displayFloorIndex;
                    const defaultUnitNumber = generateUnitNumber(floorNumber, unitIndex);
                    const unitNumber = block.customUnitNames?.[displayFloorIndex]?.[unitIndex] || defaultUnitNumber;
                    const isEditingThisUnit = editingUnit?.blockIndex === blockIndex && 
                                            editingUnit?.floorIndex === displayFloorIndex && 
                                            editingUnit?.unitIndex === unitIndex;
                    
                    const unitMenuItems = [
                      {
                        key: 'edit',
                        label: t('common:actions.edit'),
                        icon: <EditOutlined />,
                        onClick: () => handleEditUnit(blockIndex, displayFloorIndex, unitIndex)
                      },
                      {
                        key: 'delete',
                        label: t('common:actions.delete'),
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => handleDeleteUnit(blockIndex, displayFloorIndex, unitIndex)
                      }
                    ];

                    return (
                      <div
                        key={`block-${blockIndex}-floor-${floorIndex}-unit-${unitIndex}`}
                        style={{
                          minWidth: '50px',
                          height: '40px',
                          backgroundColor: isEditingThisUnit ? '#e6f7ff' : '#f0f0f0',
                          border: isEditingThisUnit ? '2px solid #1890ff' : '2px solid #d9d9d9',
                          borderRadius: '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: '500',
                          color: '#595959',
                          transition: 'all 0.2s',
                          position: 'relative',
                          flexShrink: 0,
                          cursor: 'pointer',
                          overflow: 'visible'
                        }}
                        onMouseEnter={(e) => {
                          if (!isEditingThisUnit) {
                            e.currentTarget.style.backgroundColor = '#e6f7ff';
                            e.currentTarget.style.borderColor = '#1890ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isEditingThisUnit) {
                            e.currentTarget.style.backgroundColor = '#f0f0f0';
                            e.currentTarget.style.borderColor = '#d9d9d9';
                          }
                        }}
                      >
                        {isEditingThisUnit ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', width: '100%', padding: '0 4px' }}>
                            <Input
                              size="small"
                              value={editingUnitName}
                              onChange={(e) => setEditingUnitName(e.target.value)}
                              style={{ 
                                fontSize: '8px',
                                height: '20px',
                                flex: 1
                              }}
                              onPressEnter={handleSaveUnit}
                              autoFocus
                            />
                            <Button
                              type="text"
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={handleSaveUnit}
                              style={{ color: '#52c41a', padding: '0', height: '20px', minWidth: '16px' }}
                            />
                            <Button
                              type="text"
                              size="small"
                              icon={<CloseOutlined />}
                              onClick={handleCancelUnitEdit}
                              style={{ color: '#ff4d4f', padding: '0', height: '20px', minWidth: '16px' }}
                            />
                          </div>
                        ) : (
                          <Dropdown
                            menu={{ items: unitMenuItems }}
                            trigger={['click']}
                            placement="bottomRight"
                            overlayStyle={{ zIndex: 1000 }}
                          >
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                              position: 'relative'
                            }}>
                              <div style={{ fontSize: '8px', lineHeight: '1' }}>
                                {unitNumber}
                              </div>
                              <div style={{
                                position: 'absolute',
                                top: '1px',
                                right: '1px',
                                opacity: 0.3,
                                transition: 'opacity 0.2s',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '2px',
                                padding: '1px',
                                zIndex: 1,
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.3';
                              }}
                            >
                              <MoreOutlined style={{ fontSize: '6px', color: '#666' }} />
                            </div>
                            </div>
                          </Dropdown>
                        )}
                      </div>
                    );
                  }).filter(Boolean)}
                  
                  {/* Render added units for this floor */}
                  {block.addedUnits?.[displayFloorIndex]?.map((addedUnitIndex) => {
                    const floorNumber = floors - displayFloorIndex;
                    const defaultUnitNumber = generateUnitNumber(floorNumber, addedUnitIndex);
                    const unitNumber = block.customUnitNames?.[displayFloorIndex]?.[addedUnitIndex] || defaultUnitNumber;
                    const isEditingThisUnit = editingUnit?.blockIndex === blockIndex && 
                                            editingUnit?.floorIndex === displayFloorIndex && 
                                            editingUnit?.unitIndex === addedUnitIndex;
                    
                    const unitMenuItems = [
                      {
                        key: 'edit',
                        label: t('common:actions.edit'),
                        icon: <EditOutlined />,
                        onClick: () => handleEditUnit(blockIndex, displayFloorIndex, addedUnitIndex)
                      },
                      {
                        key: 'delete',
                        label: t('common:actions.delete'),
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => handleDeleteAddedUnit(blockIndex, displayFloorIndex, addedUnitIndex)
                      }
                    ];

                    return (
                      <div
                        key={`block-${blockIndex}-floor-${floorIndex}-added-unit-${addedUnitIndex}`}
                        style={{
                          minWidth: '50px',
                          height: '40px',
                          backgroundColor: isEditingThisUnit ? '#e6f7ff' : '#f0f8ff',
                          border: isEditingThisUnit ? '2px solid #1890ff' : '2px solid #40a9ff',
                          borderRadius: '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: '500',
                          color: '#1890ff',
                          transition: 'all 0.2s',
                          position: 'relative',
                          flexShrink: 0,
                          cursor: 'pointer',
                          overflow: 'visible'
                        }}
                        onMouseEnter={(e) => {
                          if (!isEditingThisUnit) {
                            e.currentTarget.style.backgroundColor = '#e6f7ff';
                            e.currentTarget.style.borderColor = '#1890ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isEditingThisUnit) {
                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                            e.currentTarget.style.borderColor = '#40a9ff';
                          }
                        }}
                      >
                        {isEditingThisUnit ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', width: '100%', padding: '0 4px' }}>
                            <Input
                              size="small"
                              value={editingUnitName}
                              onChange={(e) => setEditingUnitName(e.target.value)}
                              style={{ 
                                fontSize: '8px',
                                height: '20px',
                                flex: 1
                              }}
                              onPressEnter={handleSaveUnit}
                              autoFocus
                            />
                            <Button
                              type="text"
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={handleSaveUnit}
                              style={{ color: '#52c41a', padding: '0', height: '20px', minWidth: '16px' }}
                            />
                            <Button
                              type="text"
                              size="small"
                              icon={<CloseOutlined />}
                              onClick={handleCancelUnitEdit}
                              style={{ color: '#ff4d4f', padding: '0', height: '20px', minWidth: '16px' }}
                            />
                          </div>
                        ) : (
                          <Dropdown
                            menu={{ items: unitMenuItems }}
                            trigger={['click']}
                            placement="bottomRight"
                            overlayStyle={{ zIndex: 1000 }}
                          >
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                              position: 'relative'
                            }}>
                              <div style={{ fontSize: '8px', lineHeight: '1' }}>
                                {unitNumber}
                              </div>
                              <div style={{
                                position: 'absolute',
                                top: '1px',
                                right: '1px',
                                opacity: 0.3,
                                transition: 'opacity 0.2s',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '2px',
                                padding: '1px',
                                zIndex: 1,
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.3';
                              }}
                            >
                              <MoreOutlined style={{ fontSize: '6px', color: '#666' }} />
                            </div>
                            </div>
                          </Dropdown>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Add Unit Dropdown */}
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'add-to-floor',
                          label: t('onboarding:cards.individualBlocks.addToThisFloor'),
                          icon: <PlusOutlined />,
                          onClick: () => handleAddUnitToFloor(blockIndex, displayFloorIndex)
                        },
                        {
                          key: 'add-to-all',
                          label: t('onboarding:cards.individualBlocks.addToAllFloors'),
                          icon: <PlusOutlined />,
                          onClick: () => handleAddUnitToAllFloors(blockIndex)
                        }
                      ]
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                    overlayStyle={{ zIndex: 1000 }}
                  >
                    <Button
                      type="dashed"
                      size="small"
                      icon={<PlusOutlined />}
                      style={{
                        minWidth: '50px',
                        height: '40px',
                        border: '2px dashed #d9d9d9',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#1890ff',
                        backgroundColor: 'transparent',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#1890ff';
                        e.currentTarget.style.backgroundColor = '#f6ffed';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d9d9d9';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {t('onboarding:cards.individualBlocks.addUnit')}
                    </Button>
                  </Dropdown>
                </div>
              </div>
            </div>
            );
          }).filter(Boolean)}
        </div>
      </div>
    );
  };

  const isCompleted = blocksData.length > 0 && blocksData.every(block => 
    block.name.trim() && block.floors >= 2 && block.unitsPerFloor >= 2
  );

  const totalUnits = blocksData.reduce((total, block) => {
    const deletedFloors = block.deletedFloors?.length || 0;
    const activeFloors = block.floors - deletedFloors;
    const baseUnits = activeFloors * block.unitsPerFloor;
    
    // Subtract deleted units
    const deletedUnitsCount = Object.values(block.deletedUnits || {}).reduce((sum, deletedUnitsForFloor) => 
      sum + deletedUnitsForFloor.length, 0
    );
    
    // Add extra units
    const addedUnitsCount = Object.values(block.addedUnits || {}).reduce((sum, addedUnitsForFloor) => 
      sum + addedUnitsForFloor.length, 0
    );
    
    return total + baseUnits - deletedUnitsCount + addedUnitsCount;
  }, 0);

  return (
    <Card
      style={{
        width: '100%',
        maxWidth: 600,
        margin: '0 auto',
        border: isCompleted ? '1px solid #52c41a' : '1px solid #1890ff',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      }}
      styles={{ body: { padding: '32px' } }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ marginBottom: '16px' }}>
          <HomeOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
          {t('onboarding:cards.individualBlocks.configureBlocks', { 
            count: numberOfBlocks, 
            plural: numberOfBlocks > 1 ? 's' : '' 
          })}
        </Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {t('onboarding:cards.individualBlocks.subtitle')}
        </Text>
        
        {/* Block Progress Indicators with Icon Buttons */}
        {(numberOfBlocks > 1 || numberOfBlocks === 1) && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '8px',
            marginTop: '24px',
            marginBottom: '20px'
          }}>
            {/* Left Arrow Button */}
            <Button
              icon={<LeftOutlined />}
              onClick={handlePreviousBlock}
              disabled={currentBlockIndex === 0}
              size="small"
              type="text"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
            
            {/* Block Number Buttons */}
            {Array.from({ length: numberOfBlocks }, (_, index) => (
              <Button
                key={index}
                type={index === currentBlockIndex ? 'primary' : 'default'}
                size="small"
                onClick={() => handleNavigateToBlock(index)}
                style={{
                  minWidth: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                {index + 1}
              </Button>
            ))}
            
            {/* Summary Indicator */}
            <Button
              type={currentBlockIndex === numberOfBlocks ? 'primary' : 'default'}
              size="small"
              onClick={() => setCurrentBlockIndex(numberOfBlocks)}
              style={{
                minWidth: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              ✓
            </Button>
            
            {/* Right Arrow Button */}
            <Button
              icon={<RightOutlined />}
              onClick={handleNextBlock}
              disabled={currentBlockIndex === numberOfBlocks}
              size="small"
              type="text"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </div>
        )}
      </div>

      {/* Block Forms or Summary */}
      {currentBlockIndex < numberOfBlocks ? (
        <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormValuesChange}
        initialValues={{
          blocks: blocksData.length > 0 ? blocksData : Array.from({ length: numberOfBlocks }, (_, index) => ({
            name: t('onboarding:cards.individualBlocks.blockNumber', { number: index + 1 }),
            floors: 2,
            unitsPerFloor: 2,
          })),
        }}
      >
        <Form.List name="blocks">
          {(fields) => (
            <div>
              {fields
                .filter((_field, index) => index === currentBlockIndex)
                .map((field, _index) => (
                <Card
                  key={field.key}
                  size="small"
                  style={{
                    marginBottom: '16px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                  }}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {editingBlockIndex === currentBlockIndex ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <Input
                            value={editingBlockName}
                            onChange={(e) => setEditingBlockName(e.target.value)}
                            size="small"
                            style={{ flex: 1 }}
                            onPressEnter={() => handleSaveBlockName(currentBlockIndex)}
                            autoFocus
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={() => handleSaveBlockName(currentBlockIndex)}
                            style={{ color: '#52c41a' }}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={handleCancelEdit}
                            style={{ color: '#ff4d4f' }}
                          />
                        </div>
                      ) : (
                        <>
                          <Text strong style={{ color: '#1890ff' }}>
                            {blocksData[currentBlockIndex]?.name || t('onboarding:cards.individualBlocks.blockNumber', { number: currentBlockIndex + 1 })}
                          </Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditBlockName(currentBlockIndex)}
                            style={{ color: '#1890ff' }}
                          />
                        </>
                      )}
                      </div>
                      
                      {/* Progress indicator for multiple blocks */}
                      {numberOfBlocks > 1 && (
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666',
                          backgroundColor: '#f0f0f0',
                          padding: '4px 8px',
                          borderRadius: '12px'
                        }}>
{t('onboarding:cards.individualBlocks.blockOf', { current: currentBlockIndex + 1, total: numberOfBlocks })}
                        </div>
                      )}
                    </div>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={0}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <Form.Item
                          key={`${field.key}-floors`}
                          name={[field.name, 'floors']}
                          rules={[{ required: true, message: t('onboarding:cards.individualBlocks.numberOfFloors') }]}
                          label={<span style={{ fontSize: '12px', color: '#666' }}>{t('onboarding:cards.individualBlocks.numberOfFloors')}:</span>}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder={t('onboarding:cards.individualBlocks.numberOfFloors')}
                            style={{ borderRadius: '6px' }}
                            options={floorOptions}
                          />
                        </Form.Item>
                      </div>

                      <div style={{ flex: 1 }}>
                        <Form.Item
                          key={`${field.key}-units`}
                          name={[field.name, 'unitsPerFloor']}
                          rules={[{ required: true, message: t('onboarding:cards.individualBlocks.unitsPerFloor') }]}
                          label={<span style={{ fontSize: '12px', color: '#666' }}>{t('onboarding:cards.individualBlocks.unitsPerFloor')}:</span>}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder={t('onboarding:cards.individualBlocks.unitsPerFloor')}
                            style={{ borderRadius: '6px' }}
                            options={unitsPerFloorOptions}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Floor Plan Section */}
                    {blocksData[currentBlockIndex] && blocksData[currentBlockIndex].floors && blocksData[currentBlockIndex].unitsPerFloor && (
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                                                      <Text type="secondary" style={{ fontSize: '12px' }}>
                              {(() => {
                                const block = blocksData[currentBlockIndex];
                                const deletedFloors = block.deletedFloors || [];
                                const deletedUnits = block.deletedUnits || {};
                                const addedUnits = block.addedUnits || {};
                                const activeFloors = block.floors - deletedFloors.length;
                                const totalUnits = Array.from({ length: block.floors }, (_, floorIndex) => {
                                  if (deletedFloors.includes(floorIndex)) return 0;
                                  const deletedUnitsForFloor = deletedUnits[floorIndex] || [];
                                  const addedUnitsForFloor = addedUnits[floorIndex] || [];
                                  return block.unitsPerFloor - deletedUnitsForFloor.length + addedUnitsForFloor.length;
                                }).reduce((sum, units) => sum + units, 0);
                                return t('onboarding:cards.individualBlocks.unitsFormula', { 
                                  floors: activeFloors, 
                                  unitsPerFloor: block.unitsPerFloor, 
                                  totalUnits: totalUnits 
                                });
                              })()}
                            </Text>
                        </div>
                        
                        {/* Unit Naming Convention Dropdown */}
                        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                          <Space direction="vertical" size="small">
                            <Select
                              value={unitNamingConvention}
                              onChange={setUnitNamingConvention}
                              style={{ width: '200px' }}
                              size="small"
                            >
                              <Select.Option value="floor-alphabet">{t('onboarding:cards.individualBlocks.unitNamingConventions.floorAlphabet')}</Select.Option>
                              <Select.Option value="floor-number">{t('onboarding:cards.individualBlocks.unitNamingConventions.floorNumber')}</Select.Option>
                              <Select.Option value="three-digit">{t('onboarding:cards.individualBlocks.unitNamingConventions.threeDigit')}</Select.Option>
                              <Select.Option value="custom">{t('onboarding:cards.individualBlocks.unitNamingConventions.custom')}</Select.Option>
                            </Select>
                            
                            {/* Custom Prefix Input */}
                            {unitNamingConvention === 'custom' && (
                              <div>
                                <Input
                                  placeholder={t('onboarding:cards.individualBlocks.customPrefix')}
                                  value={customPrefix}
                                  onChange={(e) => setCustomPrefix(e.target.value)}
                                  style={{ width: '120px' }}
                                  size="small"
                                />
                                <div style={{ marginTop: '4px' }}>
                                  <Text type="secondary" style={{ fontSize: '10px' }}>
                                    {t('onboarding:cards.individualBlocks.customPrefixExample', { prefix: customPrefix || '8' })}
                                  </Text>
                                </div>
                              </div>
                            )}
                          </Space>
                        </div>
                        {renderApartmentGrid(currentBlockIndex)}
                      </div>
                    )}
                  </Space>
                </Card>
              ))}
            </div>
          )}
        </Form.List>

        {/* Action Buttons */}
        {currentBlockIndex < numberOfBlocks && (
          <div style={{ textAlign: 'center' }}>
            <Button 
              type="primary" 
              size="large"
              loading={isSubmitting}
              onClick={handleSubmit}
              disabled={!isCompleted}
              style={{ 
                minWidth: '120px',
                backgroundColor: isCompleted ? '#52c41a' : '#1890ff',
                borderColor: isCompleted ? '#52c41a' : '#1890ff',
                color: '#fff',
                fontWeight: 'bold'
              }}
            >
{(() => {
                if (!isCompleted) return t('onboarding:cards.individualBlocks.next');
                if (currentBlockIndex < numberOfBlocks - 1) return t('onboarding:cards.individualBlocks.next');
                if (currentBlockIndex === numberOfBlocks - 1) return t('onboarding:cards.individualBlocks.summary.buttons.createBlocks', { count: numberOfBlocks, plural: numberOfBlocks > 1 ? 's' : '' });
                return t('onboarding:cards.individualBlocks.next');
              })()}
            </Button>
          </div>
        )}


      </Form>
      ) : (
        /* Summary Section */
        <div>
          {/* Block Details Table */}
          <div style={{ marginBottom: '24px' }}>
            <Title level={4} style={{ textAlign: 'center', marginBottom: '16px', color: '#1890ff' }}>
              {t('onboarding:cards.individualBlocks.blockConfiguration')}
            </Title>
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '8px', 
              overflow: 'hidden',
              backgroundColor: '#fff'
            }}>
              {/* Table Header */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr', 
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #d9d9d9'
              }}>
                <div style={{ padding: '12px 16px', fontWeight: 'bold', borderRight: '1px solid #d9d9d9' }}>
                  {t('onboarding:cards.individualBlocks.summary.tableHeaders.blockName')}
                </div>
                <div style={{ padding: '12px 16px', fontWeight: 'bold', borderRight: '1px solid #d9d9d9', textAlign: 'center' }}>
                  {t('onboarding:cards.individualBlocks.summary.tableHeaders.floors')}
                </div>
                <div style={{ padding: '12px 16px', fontWeight: 'bold', textAlign: 'center' }}>
                  {t('onboarding:cards.individualBlocks.summary.tableHeaders.units')}
                </div>
              </div>
              
              {/* Table Rows */}
              {blocksData.map((block, index) => {
                const deletedFloors = block.deletedFloors?.length || 0;
                const activeFloors = block.floors - deletedFloors;
                const deletedUnits = block.deletedUnits || {};
                const addedUnits = block.addedUnits || {};
                
                const blockUnits = Array.from({ length: block.floors }, (_, floorIndex) => {
                  if (block.deletedFloors?.includes(floorIndex)) return 0;
                  const deletedUnitsForFloor = deletedUnits[floorIndex] || [];
                  const addedUnitsForFloor = addedUnits[floorIndex] || [];
                  return block.unitsPerFloor - deletedUnitsForFloor.length + addedUnitsForFloor.length;
                }).reduce((sum, units) => sum + units, 0);

                return (
                  <div key={index} style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr 1fr',
                    borderBottom: index < blocksData.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ padding: '12px 16px', borderRight: '1px solid #f0f0f0' }}>
                      <Text strong>{block.name}</Text>
                    </div>
                    <div style={{ padding: '12px 16px', borderRight: '1px solid #f0f0f0', textAlign: 'center' }}>
                      <Text>{activeFloors}</Text>
                    </div>
                    <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>{blockUnits}</Text>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Statistics */}
          <div style={{ marginBottom: '20px' }}>
            <Row gutter={[16, 16]} justify="center">
              <Col xs={12} sm={6}>
                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>{t('onboarding:cards.individualBlocks.summary.statistics.totalBlocks')}</Text>
                  <br />
                  <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>{numberOfBlocks}</Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>{t('onboarding:cards.individualBlocks.summary.statistics.totalUnits')}</Text>
                  <br />
                  <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>{totalUnits}</Text>
                </div>
              </Col>
            </Row>
          </div>



          {/* Parking Option */}
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'center'
          }}>
            <Checkbox
              checked={includeParking}
              onChange={(e) => setIncludeParking(e.target.checked)}
              style={{ fontSize: '14px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CarOutlined style={{ color: '#666', fontSize: '14px' }} />
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {t('onboarding:cards.individualBlocks.summary.parkingOption')}
                </Text>
              </div>
            </Checkbox>
          </div>

          {/* Summary Action Buttons */}
          <div style={{ textAlign: 'center' }}>
            <Space size="large">
              <Button size="large" onClick={handleSummaryBack}>
                {t('onboarding:cards.individualBlocks.summary.buttons.backToBlocks')}
              </Button>
              <Button 
                type="primary" 
                size="large" 
                onClick={handleSummaryNext}
                loading={isSubmitting}
                disabled={isSubmitting}
                style={{ 
                  minWidth: '150px',
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a'
                }}
              >
                {t('onboarding:cards.individualBlocks.summary.buttons.createBlocks', { 
                  count: numberOfBlocks, 
                  plural: numberOfBlocks > 1 ? 's' : '' 
                })}
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Card>
  );
};
